import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  Plus, 
  Edit, 
  Trash2, 
  CreditCard, 
  Settings, 
  CheckCircle, 
  XCircle,
  AlertCircle,
  Loader2
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { PaymentTypes } from '@/api/client';

const PaymentTypesSettings = () => {
  const { t } = useTranslation();
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingType, setEditingType] = useState(null);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    code: '',
    provider: 'tbank',
    description: '',
    priority: 1,
    supported_currencies: ['RUB'],
    supported_regions: ['RU'],
    integration_config: {
      terminal_key: '',
      secret_key: '',
      api_url: '',
      client_id: '',
      client_secret: '',
      success_url: '',
      fail_url: '',
      cancel_url: '',
      webhook_url: '',
      environment: 'sandbox',
      taxation: 'usn_income',
      features: {}
    }
  });

  const providers = [
    { value: 'tbank', label: t('payments.providers.tbank'), description: t('payments.providers.tbank_desc') },
    { value: 'paypal', label: t('payments.providers.paypal'), description: t('payments.providers.paypal_desc') },
    { value: 'custom', label: t('payments.providers.custom'), description: t('payments.providers.custom_desc') }
  ];

  const currencies = [
    { code: 'RUB', symbol: '₽', name: t('payments.currencies.rub') },
    { code: 'USD', symbol: '$', name: t('payments.currencies.usd') },
    { code: 'EUR', symbol: '€', name: t('payments.currencies.eur') }
  ];

  const regions = [
    { code: 'RU', name: t('payments.regions.ru') },
    { code: 'US', name: t('payments.regions.us') },
    { code: 'EU', name: t('payments.regions.eu') },
    { code: 'CN', name: t('payments.regions.cn') }
  ];

  const tbankFeatures = [
    { key: 'sbp', label: t('payments.features.sbp') },
    { key: 'tpay', label: t('payments.features.tpay') },
    { key: 'cards', label: t('payments.features.cards') },
    { key: 'mirpay', label: t('payments.features.mirpay') },
    { key: 'qr_code', label: t('payments.features.qr_code') },
    { key: 'quick_payment', label: t('payments.features.quick_payment') },
    { key: 'iframe_enabled', label: t('payments.features.iframe_enabled') }
  ];

  const paypalFeatures = [
    { key: '3d_secure', label: t('payments.features.3d_secure') },
    { key: 'installments', label: t('payments.features.installments') },
    { key: 'paypal_credit', label: t('payments.features.paypal_credit') },
    { key: 'guest_checkout', label: t('payments.features.guest_checkout') },
    { key: 'express_checkout', label: t('payments.features.express_checkout') }
  ];

  const loadPaymentTypes = async () => {
    try {
      setIsLoading(true);
      const data = await PaymentTypes.listAdmin();
      setPaymentTypes(data);
    } catch (error) {
      console.error('Error loading payment types:', error);
      setError(t('payments.messages.error_loading'));
    } finally {
      setIsLoading(false);
    }
  };

  const savePaymentType = async () => {
    try {
      setIsSaving(true);
      
      if (editingType) {
        await PaymentTypes.update(editingType.id, formData);
      } else {
        await PaymentTypes.create(formData);
      }

      await loadPaymentTypes();
      resetForm();
      setIsDialogOpen(false);
    } catch (error) {
      console.error('Error saving payment type:', error);
      setError(t('payments.messages.error_saving'));
    } finally {
      setIsSaving(false);
    }
  };

  const toggleActive = async (id, isActive) => {
    try {
      await PaymentTypes.toggleActive(id, isActive);
      await loadPaymentTypes();
    } catch (error) {
      console.error('Error toggling payment type:', error);
      setError(t('payments.messages.error_toggle'));
    }
  };

  const deletePaymentType = async (id) => {
    if (!confirm(t('payments.messages.delete_confirm'))) return;
    
    try {
      await PaymentTypes.delete(id);
      await loadPaymentTypes();
    } catch (error) {
      console.error('Error deleting payment type:', error);
      setError(t('payments.messages.error_deleting'));
    }
  };

  const resetForm = () => {
    const defaultUrls = getDefaultUrlsForProvider('tbank');
    setFormData({
      name: '',
      code: '',
      provider: 'tbank',
      description: '',
      priority: 1,
      supported_currencies: ['RUB'],
      supported_regions: ['RU'],
      integration_config: {
        terminal_key: '',
        secret_key: '',
        api_url: '',
        client_id: '',
        client_secret: '',
        environment: 'sandbox',
        taxation: 'usn_income',
        features: {},
        ...defaultUrls
      }
    });
    setEditingType(null);
    setError(null);
  };

  const openEditDialog = (paymentType) => {
    setEditingType(paymentType);
    setFormData({
      name: paymentType.name,
      code: paymentType.code,
      provider: paymentType.provider,
      description: paymentType.description,
      priority: paymentType.priority,
      supported_currencies: paymentType.supported_currencies || [],
      supported_regions: paymentType.supported_regions || [],
      integration_config: {
        terminal_key: '',
        secret_key: '',
        api_url: '',
        client_id: '',
        client_secret: '',
        success_url: '/payment/success',
        fail_url: '/payment/fail',
        cancel_url: '/payment/cancel',
        webhook_url: '',
        environment: 'sandbox',
        taxation: 'usn_income',
        features: {},
        ...paymentType.integration_config
      }
    });
    setIsDialogOpen(true);
  };

  const openCreateDialog = () => {
    resetForm();
    setIsDialogOpen(true);
  };

  const handleFeatureToggle = (featureKey, checked) => {
    setFormData(prev => ({
      ...prev,
      integration_config: {
        ...prev.integration_config,
        features: {
          ...prev.integration_config.features,
          [featureKey]: checked
        }
      }
    }));
  };

  const getFeaturesForProvider = (provider) => {
    switch (provider) {
      case 'tbank': return tbankFeatures;
      case 'paypal': return paypalFeatures;
      default: return [];
    }
  };

  const getDefaultUrlsForProvider = (provider) => {
    const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
    
    switch (provider) {
      case 'tbank':
        return {
          api_url: 'https://securepay.tinkoff.ru/v2/',
          success_url: `${baseUrl}/order/{orderId}?tbank-payment=success`,
          fail_url: `${baseUrl}/order/{orderId}?tbank-payment=fail`,
          cancel_url: `${baseUrl}/order/{orderId}?tbank-payment=cancel`,
          webhook_url: '/api/v1/payments/tbank/webhook'
        };
      case 'paypal':
        return {
          api_url: 'https://api.sandbox.paypal.com',
          success_url: '/payment/success',
          fail_url: '/payment/fail',
          cancel_url: '/payment/cancel',
          webhook_url: '/api/v1/payments/paypal/webhook'
        };
      default:
        return {
          api_url: '',
          success_url: '/payment/success',
          fail_url: '/payment/fail',
          cancel_url: '/payment/cancel',
          webhook_url: ''
        };
    }
  };

  useEffect(() => {
    loadPaymentTypes();
  }, []);

  if (isLoading) {
    return (
      <Card className="elegant-card">
                 <CardContent className="flex items-center justify-center py-8">
           <Loader2 className="w-6 h-6 animate-spin mr-2" />
           {t('payments.types.loading')}
         </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="elegant-card">
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-amber-600" />
              {t('payments.types.title')}
            </div>
            <Button onClick={openCreateDialog} variant="outline" size="sm">
              <Plus className="w-4 h-4 mr-2" />
              {t('payments.types.add')}
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-600 mb-6">
            {t('payments.description')}
          </p>

          {error && (
            <Alert className="mb-4">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-4">
            {paymentTypes.map((type) => (
              <div key={type.id} className="p-4 bg-white border border-slate-200 rounded-lg">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <h3 className="font-semibold text-slate-900">{type.name}</h3>
                                         <Badge className={type.is_active ? "bg-green-600 text-white border-green-700" : "bg-gray-600 text-white border-gray-700"}>
                       {type.is_active ? t('payments.types.active') : t('payments.types.inactive')}
                     </Badge>
                    <Badge variant="outline">{type.provider}</Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <Switch
                      checked={type.is_active}
                      onCheckedChange={(checked) => toggleActive(type.id, checked)}
                    />
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEditDialog(type)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => deletePaymentType(type.id)}
                    >
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
                
                <p className="text-sm text-slate-600 mb-3">{type.description}</p>
                
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-medium">{t('payments.form.priority')}:</span> {type.priority}
                  </div>
                  <div>
                    <span className="font-medium">{t('payments.form.currencies')}:</span> {type.supported_currencies?.join(', ')}
                  </div>
                  <div>
                    <span className="font-medium">{t('payments.form.regions')}:</span> {type.supported_regions?.join(', ')}
                  </div>
                  <div>
                    <span className="font-medium">{t('payments.form.features')}:</span> {Object.keys(type.features || {}).length}
                  </div>
                </div>
              </div>
            ))}

                         {paymentTypes.length === 0 && (
               <div className="text-center py-8 text-slate-500">
                 {t('payments.types.not_found')}
               </div>
             )}
          </div>
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
                       <DialogTitle>
             {editingType ? t('payments.types.edit') : t('payments.types.create')}
           </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="name">{t('payments.form.name')} *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="T-Bank"
                />
              </div>
              <div>
                <Label htmlFor="code">{t('payments.form.code')} *</Label>
                <Input
                  id="code"
                  value={formData.code}
                  onChange={(e) => setFormData(prev => ({ ...prev, code: e.target.value }))}
                  placeholder="tbank"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="provider">{t('payments.form.provider')} *</Label>
              <Select
                value={formData.provider}
                onValueChange={(value) => {
                  const defaultUrls = getDefaultUrlsForProvider(value);
                  setFormData(prev => ({ 
                    ...prev, 
                    provider: value,
                    integration_config: { 
                      ...prev.integration_config, 
                      features: {},
                      ...defaultUrls
                    }
                  }));
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                                 <SelectContent>
                   {providers.map(provider => (
                     <SelectItem key={provider.value} value={provider.value}>
                       {t(`payments.providers.${provider.value}`)}
                     </SelectItem>
                   ))}
                 </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="description">{t('payments.form.description')}</Label>
              <Textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder={t('payments.form.description')}
              />
            </div>

            <div>
              <Label htmlFor="priority">{t('payments.form.priority')}</Label>
              <Input
                id="priority"
                type="number"
                value={formData.priority}
                onChange={(e) => setFormData(prev => ({ ...prev, priority: parseInt(e.target.value) || 1 }))}
                min="1"
              />
            </div>

            <div>
              <Label>{t('payments.form.currencies')}</Label>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {currencies.map(currency => (
                  <div key={currency.code} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id={`currency-${currency.code}`}
                      checked={formData.supported_currencies.includes(currency.code)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData(prev => ({
                            ...prev,
                            supported_currencies: [...prev.supported_currencies, currency.code]
                          }));
                        } else {
                          setFormData(prev => ({
                            ...prev,
                            supported_currencies: prev.supported_currencies.filter(c => c !== currency.code)
                          }));
                        }
                      }}
                    />
                    <Label htmlFor={`currency-${currency.code}`} className="text-sm">
                      {currency.symbol} {currency.code}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <Label>{t('payments.form.regions')}</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {regions.map(region => (
                  <div key={region.code} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id={`region-${region.code}`}
                      checked={formData.supported_regions.includes(region.code)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setFormData(prev => ({
                            ...prev,
                            supported_regions: [...prev.supported_regions, region.code]
                          }));
                        } else {
                          setFormData(prev => ({
                            ...prev,
                            supported_regions: prev.supported_regions.filter(r => r !== region.code)
                          }));
                        }
                      }}
                    />
                    <Label htmlFor={`region-${region.code}`} className="text-sm">
                      {region.name}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            {formData.provider === 'tbank' && (
              <div className="space-y-4">
                <h4 className="font-medium">{t('payments.form.integration')} T-Bank</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="terminal_key">Terminal Key</Label>
                    <Input
                      id="terminal_key"
                      value={formData.integration_config.terminal_key || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        integration_config: {
                          ...prev.integration_config,
                          terminal_key: e.target.value
                        }
                      }))}
                      placeholder="1234567890"
                    />
                  </div>
                  <div>
                    <Label htmlFor="secret_key">Secret Key</Label>
                    <Input
                      id="secret_key"
                      type="password"
                      value={formData.integration_config.secret_key || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        integration_config: {
                          ...prev.integration_config,
                          secret_key: e.target.value
                        }
                      }))}
                      placeholder="secret_key_here"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="api_url">API URL</Label>
                    <Input
                      id="api_url"
                      value={formData.integration_config.api_url || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        integration_config: {
                          ...prev.integration_config,
                          api_url: e.target.value
                        }
                      }))}
                      placeholder="https://securepay.tinkoff.ru/v2/"
                    />
                  </div>
                  <div>
                    <Label htmlFor="taxation">{t('payments.form.taxation')}</Label>
                    <Select
                      value={formData.integration_config.taxation || 'usn_income'}
                      onValueChange={(value) => setFormData(prev => ({
                        ...prev,
                        integration_config: {
                          ...prev.integration_config,
                          taxation: value
                        }
                      }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                                           <SelectContent>
                       <SelectItem value="usn_income">{t('payments.taxation.usn_income')}</SelectItem>
                       <SelectItem value="usn_income_outcome">{t('payments.taxation.usn_income_outcome')}</SelectItem>
                       <SelectItem value="patent">{t('payments.taxation.patent')}</SelectItem>
                       <SelectItem value="esn">{t('payments.taxation.esn')}</SelectItem>
                       <SelectItem value="osn">{t('payments.taxation.osn')}</SelectItem>
                     </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="success_url">{t('payments.form.success_url')}</Label>
                    <Input
                      id="success_url"
                      value={formData.integration_config.success_url || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        integration_config: {
                          ...prev.integration_config,
                          success_url: e.target.value
                        }
                      }))}
                      placeholder="/payment/success"
                    />
                  </div>
                  <div>
                    <Label htmlFor="fail_url">{t('payments.form.fail_url')}</Label>
                    <Input
                      id="fail_url"
                      value={formData.integration_config.fail_url || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        integration_config: {
                          ...prev.integration_config,
                          fail_url: e.target.value
                        }
                      }))}
                      placeholder="/payment/fail"
                    />
                  </div>
                  <div>
                    <Label htmlFor="webhook_url">Webhook URL</Label>
                    <Input
                      id="webhook_url"
                      value={formData.integration_config.webhook_url || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        integration_config: {
                          ...prev.integration_config,
                          webhook_url: e.target.value
                        }
                      }))}
                      placeholder="/api/v1/payments/webhook/tbank"
                    />
                  </div>
                </div>
              </div>
            )}

            {formData.provider === 'paypal' && (
              <div className="space-y-4">
                <h4 className="font-medium">{t('payments.form.integration')} PayPal</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="client_id">Client ID</Label>
                    <Input
                      id="client_id"
                      value={formData.integration_config.client_id || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        integration_config: {
                          ...prev.integration_config,
                          client_id: e.target.value
                        }
                      }))}
                      placeholder="client_id_here"
                    />
                  </div>
                  <div>
                    <Label htmlFor="client_secret">Client Secret</Label>
                    <Input
                      id="client_secret"
                      type="password"
                      value={formData.integration_config.client_secret || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        integration_config: {
                          ...prev.integration_config,
                          client_secret: e.target.value
                        }
                      }))}
                      placeholder="client_secret_here"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="environment">{t('payments.form.environment')}</Label>
                    <Select
                      value={formData.integration_config.environment || 'sandbox'}
                      onValueChange={(value) => setFormData(prev => ({
                        ...prev,
                        integration_config: {
                          ...prev.integration_config,
                          environment: value
                        }
                      }))}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                                           <SelectContent>
                       <SelectItem value="sandbox">{t('payments.environments.sandbox')}</SelectItem>
                       <SelectItem value="live">{t('payments.environments.live')}</SelectItem>
                     </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="webhook_id">Webhook ID</Label>
                    <Input
                      id="webhook_id"
                      value={formData.integration_config.webhook_id || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        integration_config: {
                          ...prev.integration_config,
                          webhook_id: e.target.value
                        }
                      }))}
                      placeholder="webhook_id_here"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="return_url">{t('payments.form.return_url')}</Label>
                    <Input
                      id="return_url"
                      value={formData.integration_config.return_url || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        integration_config: {
                          ...prev.integration_config,
                          return_url: e.target.value
                        }
                      }))}
                      placeholder="/payment/success"
                    />
                  </div>
                  <div>
                    <Label htmlFor="cancel_url">{t('payments.form.cancel_url')}</Label>
                    <Input
                      id="cancel_url"
                      value={formData.integration_config.cancel_url || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        integration_config: {
                          ...prev.integration_config,
                          cancel_url: e.target.value
                        }
                      }))}
                      placeholder="/payment/cancel"
                    />
                  </div>
                  <div>
                    <Label htmlFor="webhook_url">Webhook URL</Label>
                    <Input
                      id="webhook_url"
                      value={formData.integration_config.webhook_url || ''}
                      onChange={(e) => setFormData(prev => ({
                        ...prev,
                        integration_config: {
                          ...prev.integration_config,
                          webhook_url: e.target.value
                        }
                      }))}
                      placeholder="/api/v1/payments/webhook/paypal"
                    />
                  </div>
                </div>
              </div>
            )}

                         <div>
               <Label>{t('payments.form.features')}</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {getFeaturesForProvider(formData.provider).map(feature => (
                  <div key={feature.key} className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id={`feature-${feature.key}`}
                      checked={formData.integration_config.features[feature.key] || false}
                      onChange={(e) => handleFeatureToggle(feature.key, e.target.checked)}
                    />
                                         <Label htmlFor={`feature-${feature.key}`} className="text-sm">
                       {t(`payments.features.${feature.key}`)}
                     </Label>
                  </div>
                ))}
              </div>
            </div>
          </div>

                     <div className="flex justify-end gap-2 pt-4">
             <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
               {t('payments.types.cancel')}
             </Button>
             <Button onClick={savePaymentType} disabled={isSaving}>
               {isSaving && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
               {editingType ? t('payments.types.save') : t('payments.types.create')}
             </Button>
           </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentTypesSettings;
